import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  checkTenantSite,
  TenantCheckResponse,
  type TenantCheckPortal,
} from '@/services/tenants';
import { isUnansweredRequestError } from '@/utils/unansweredRequest';

export const useTenantSiteCheck = (
  siteType: string | undefined,
  portal: TenantCheckPortal = 'clinic'
) => {
  const query = useQuery<TenantCheckResponse>({
    queryKey: ['tenants', 'check', siteType, portal],
    queryFn: () => checkTenantSite(siteType || '', portal),
    enabled: !!siteType,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  /**
   * The check got no answer (connection down, server out of reach): the site is not known to be
   * invalid, so a login screen keeps its form instead of 「無効なサイト」, and the check runs again
   * once the browser is back online.
   */
  const isUnreachable = query.isError && isUnansweredRequestError(query.error);
  const { refetch } = query;
  useEffect(() => {
    if (!isUnreachable) return undefined;
    const retry = () => void refetch();
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [isUnreachable, refetch]);

  return { ...query, isUnreachable };
};
